import { DataGrid } from "@msuite/ui/data-grid";
import { Divider } from "@msuite/ui/divider";
import { useTranslation } from "@msuite/ui/localization";
import { StatusIndicator } from "@msuite/ui/status-indicator";
import { Tag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { WebfleetOrder } from "@msuite/utils/types";
import {
	getWebfleetOrderStateColor,
	getWebfleetOrderStateTranslationKey,
	getWebfleetOrderTypeColor,
	getWebfleetOrderTypeTranslationKey,
} from "@msuite/utils/vehicles";

/** Props Interface */
interface VehicleOrderCardProps {
	order: WebfleetOrder;
}

export function VehicleOrderCard({ order }: VehicleOrderCardProps) {
	const { t } = useTranslation();

	/** Render */
	return (
		<div className="card v-stack">
			<div className="h-stack text-sm justify-between">
				<p>{order.drivername || t("vehicles.unknown_driver")}</p>
				<p>
					{t("general.last_updated")}: {moment(order.orderstate_time).longFormat()}
				</p>
			</div>
			<Divider variant="card" />
			<div className="h-stack justify-between">
				<p className="tabular-nums label">
					{t("general_fields.id")}: {order.orderid}
				</p>
				<Tag
					color={getWebfleetOrderTypeColor(order.ordertype)}
					variant="plain"
				>
					{t(getWebfleetOrderTypeTranslationKey(order.ordertype))}
				</Tag>
			</div>
			<Divider variant="card" />
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("webfleet.order_states.order_state")}</DataGrid.Label>
					<DataGrid.Value>
						<div className="h-stack">
							<StatusIndicator color={getWebfleetOrderStateColor(order.orderstate)} />
							<p>{t(getWebfleetOrderStateTranslationKey(order.orderstate))}</p>
						</div>
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("vehicles.start")}</DataGrid.Label>
					<DataGrid.Value>{guardString(order.orderstate_postext)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("vehicles.destination")}</DataGrid.Label>
					<DataGrid.Value>{guardString(order.destination)} </DataGrid.Value>
				</DataGrid.Row>
				{order.estimated_arrival_time && (
					<DataGrid.Row>
						<DataGrid.Label>{t("vehicles.expected_arrival")}</DataGrid.Label>
						<DataGrid.Value>{moment(order.estimated_arrival_time).longFormat()}</DataGrid.Value>
					</DataGrid.Row>
				)}
				{order.ordertext && (
					<DataGrid.Row>
						<DataGrid.Label>{t("webfleet.ordertext")}</DataGrid.Label>
						<DataGrid.Value>{guardString(order.ordertext)}</DataGrid.Value>
					</DataGrid.Row>
				)}
			</DataGrid>
		</div>
	);
}
